<template>
  <div class="flex justify-center items-center">
    <label
      class="cursor-pointer rounded bg-action hover:bg-action-hover text-inverse-emphasis py-[6px] px-[20px] gap-2 font-sm inline-flex items-center">
      <input type="file" :multiple="false" :accept="imageTypes" @change="onFileChange" name="eventLogoFile"
        class="hidden" />
      <span class="">
        <i class="fas fa-upload"></i>
      </span>
      <span class="font-xs">{{ label }}</span>
    </label>
  </div>
</template>
<script setup lang="ts">
import { inject, ref, watch, onBeforeUnmount } from "vue";
import * as Sentry from "@sentry/vue";
import { ValidationContext, ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import useManageFile from "@/modules/useManageFile";
import { useNotificationInjection } from "@/composables/useNotification";

const notify = useNotificationInjection();

const {
  isValidType,
  uploadEventFile,
  getEventFile,
} = useManageFile();


const props = defineProps<{
  required: boolean;
  eventId: string;
  modelValue?: string | null;
  name: string;
  label: string;
  imageTypes: string;
}>();

const innerValue = ref(props.modelValue);
const fileUrl = ref<string | null>(null);
const inputFile = ref<File | null>(null);

const emit = defineEmits(["update:modelValue"]);

// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredField, registerValidator } = useFormValidation(context);

const validateInput = () => {
  if (props.required) {
    validateRequiredField(props.name, props.label,innerValue.value ?? "");
  }
};

registerValidator(props.name, validateInput);


if (props.modelValue) {
  getEventFile(
    props.eventId,
    props.modelValue
  )
    .then((url) => {
      fileUrl.value = url;
    })
}

inputFile.value = { name: props.modelValue ?? "" } as unknown as File;

const uploadFile = async (logoFile: File) => {
  try {
    await uploadEventFile(props.eventId as string, logoFile);
    fileUrl.value = await getEventFile(props.eventId as string, logoFile.name);
  } catch (error) {
    Sentry.captureException(error);
  }
};

const onFileChange = async (event: any) => {
  const value = event.target.files || event.dataTransfer.files;
  if (value.length === 0) {
    if (!inputFile.value) return;
    inputFile.value = null;
  } else {
    const file = value[0];
    if (isValidType(file, props.imageTypes)) {
      await uploadFile(file);
      inputFile.value = file;
      emit("update:modelValue", file.name);
    } else {
      if (inputFile.value) {
        inputFile.value = null;
      }
      // Display error notification for invalid file type
      notify.error("Invalid file format", `Accepted formats: ${props.imageTypes}`);
    }
  }
};
</script>